import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, collections } from '@/lib/firebase';
import { CircleDot } from 'lucide-react';
import { useUserData } from '@/hooks/useUserData';
import { toast } from 'react-hot-toast';

export default function ReportToggle() {
  const { currentUser } = useAuth();
  const { userData, loading, error } = useUserData();
  const [updating, setUpdating] = useState(false);

  const handleToggle = async () => {
    if (!currentUser || updating || !userData) return;

    try {
      setUpdating(true);
      const userRef = doc(db, collections.users, currentUser.uid);
      
      await updateDoc(userRef, {
        reportAvailable: !userData.reportAvailable,
        updatedAt: serverTimestamp()
      });

      toast.success(`Report availability ${!userData.reportAvailable ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling report status:', error);
      toast.error('Failed to update report status');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-100 rounded-lg p-6 h-[104px]"></div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 rounded-lg p-6">
        {error}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Report Availability</h3>
        <p className="text-sm text-gray-600 mt-1">
          Toggle whether the report is available to the user
        </p>
        <p className="text-sm font-medium mt-2">
          Current status:{' '}
          <span className={userData?.reportAvailable ? 'text-green-600' : 'text-gray-600'}>
            {userData?.reportAvailable ? 'Available' : 'Not Available'}
          </span>
        </p>
      </div>

      <button
        onClick={handleToggle}
        disabled={updating}
        className={`relative inline-flex h-10 w-16 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
          userData?.reportAvailable ? 'bg-primary' : 'bg-gray-200'
        } ${updating ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span
          className={`absolute transform transition-transform duration-300 ${
            userData?.reportAvailable ? 'translate-x-8' : 'translate-x-1'
          }`}
        >
          <CircleDot className="h-8 w-8 text-white" />
        </span>
      </button>
    </div>
  );
}